import { useMemo, useState } from 'react';
import { CheckIcon, CopySimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { CATEGORY_PICKER_DRAWER_CONTENT_CLASS } from '@/components/categories/CategoryPickerDrawer';
import { SearchBar } from '@/components/transactions/screen/SearchBar';
import { Button } from '@/components/ui/button';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useGroupingCandidates } from '@/hooks/queries/useGroupingCandidates';
import { cn } from '@/lib/utils';
import type { InvestmentItem } from '@/types';
import { useManualGrouping } from './useManualGrouping';

interface Props {
  /** The position the group is anchored to; null keeps the drawer closed. */
  anchor: InvestmentItem | null;
  onClose: () => void;
  onGrouped: () => void;
}

export function InvestmentGroupPickerDrawer({ anchor, onClose, onGrouped }: Props) {
  const formatCurrency = useFormatCurrency();
  const { saving, merge } = useManualGrouping(onGrouped);
  const [query, setQuery] = useState('');
  // Keyed by id → the selected item itself, not just its id: searching swaps the candidate list,
  // so a selection made under a different query must survive to be merged.
  const [selected, setSelected] = useState<Map<string, InvestmentItem>>(new Map());

  // Candidates come from the whole portfolio (server-searched), not the current list page, so a
  // look-alike holding on another page — the weak-fingerprint case — is reachable.
  const { data: fetched, isLoading } = useGroupingCandidates(anchor !== null, query);

  const candidates = useMemo(() => {
    if (!anchor) return [];
    // The endpoint already returns ungrouped (groupId null), non-redeemed, query-matched
    // positions; only the anchor itself has to be filtered out here.
    return (fetched ?? []).filter((position) => position.id !== anchor.id && !position.groupId);
  }, [anchor, fetched]);

  function handleOpenChange(open: boolean) {
    if (!open && !saving) {
      setQuery('');
      setSelected(new Map());
      onClose();
    }
  }

  function toggle(candidate: InvestmentItem) {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(candidate.id)) next.delete(candidate.id);
      else next.set(candidate.id, candidate);
      return next;
    });
  }

  async function group(role: 'MIRROR' | 'ADDITIVE') {
    if (!anchor) return;
    // From the selection map, not the current candidate list — a pick made under an earlier query
    // is no longer in `candidates` but is still selected.
    const picked = [...selected.values()];
    if (picked.length === 0) return;
    const ok = await merge(anchor, picked, role);
    if (ok) {
      setQuery('');
      setSelected(new Map());
      onClose();
    }
  }

  const groupMirror = () => void group('MIRROR');
  const groupAdditive = () => void group('ADDITIVE');
  const canGroup = selected.size > 0 && !saving;

  return (
    <Drawer open={anchor !== null} onOpenChange={handleOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className={cn(CATEGORY_PICKER_DRAWER_CONTENT_CLASS, 'bg-surface')}
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="shrink-0 px-5 py-4 text-left! group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left!">
          <DrawerTitle className="text-lg font-semibold text-text-primary">Agrupar posições</DrawerTitle>
          <DrawerDescription className="sr-only">
            Escolha as posições que formam a mesma aplicação de {anchor?.name}.
          </DrawerDescription>
          {anchor && (
            <div className="mt-2 flex min-w-0 items-center gap-2 text-sm/5 text-text-secondary sm:text-xs">
              <CopySimpleIcon className="size-4 shrink-0 sm:size-3.5" weight="duotone" />
              <span className="min-w-0 flex-1 truncate">{anchor.name}</span>
              <span className="shrink-0 text-text-tertiary">·</span>
              <span className="shrink-0 font-mono tabular-nums">
                <MoneyValue text={formatCurrency(anchor.balance)} />
              </span>
            </div>
          )}
        </DrawerHeader>

        <div className="shrink-0 border-y border-border px-4 py-3 sm:px-5">
          <SearchBar value={query} onChange={setQuery} placeholder="Buscar posição" disabled={saving} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2" aria-busy={isLoading}>
          {isLoading && candidates.length === 0 ? (
            <div className="space-y-1 p-1">
              {[0, 1, 2].map((row) => (
                <div key={row} className="h-14 animate-pulse rounded-xl bg-surface-sunken" />
              ))}
            </div>
          ) : candidates.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-sunken">
                <MagnifyingGlassIcon className="size-5 text-text-tertiary" weight="duotone" />
              </div>
              <p className="mt-3 text-sm text-text-secondary">
                {query ? 'Nenhuma posição encontrada' : 'Nenhuma outra posição para agrupar'}
              </p>
            </div>
          ) : (
            <ul className="space-y-1">
              {candidates.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    disabled={saving}
                    aria-pressed={selected.has(candidate.id)}
                    onClick={() => toggle(candidate)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                      selected.has(candidate.id) ? 'bg-brand-50' : 'hover:bg-surface-sunken',
                    )}
                  >
                    {/* Non-interactive: the row button owns the toggle (aria-pressed); a nested
                        checkbox would be invalid interactive-in-button markup. */}
                    <span
                      aria-hidden
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                        selected.has(candidate.id)
                          ? 'border-primary bg-primary text-white'
                          : 'border-border',
                      )}
                    >
                      {selected.has(candidate.id) && <CheckIcon className="size-3" weight="bold" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-text-primary">{candidate.name}</span>
                      <span className="block truncate text-xs text-text-tertiary">{candidate.institution ?? 'Sem instituição'}</span>
                    </span>
                    <span className="shrink-0 font-mono text-sm text-text-secondary tabular-nums">
                      <MoneyValue text={formatCurrency(candidate.balance)} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* The role is asked here, in plain language, because the app cannot guess whether the
            balances are the same money seen twice or different money to be summed. */}
        <div className="shrink-0 border-t border-border p-4 sm:px-5">
          <p className="mb-2 text-2xs text-text-tertiary">Como tratar os saldos?</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button disabled={!canGroup} onClick={groupMirror} className="w-full">
              É o mesmo dinheiro
            </Button>
            <Button variant="outline" disabled={!canGroup} onClick={groupAdditive} className="w-full">
              Aplicações diferentes
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
